import { api } from './api';
import { ContattoRubrica } from '../types';

/**
 * Request payload for creating/updating contatti
 */
export interface ContattoRequest {
  nome: string;
  cognome?: string;
  azienda?: string;
  email?: string;
  telefono?: string;
  partitaIva?: string;
  codiceFiscale?: string;
  indirizzo?: string;
  citta?: string;
  provincia?: string;
  cap?: string;
  note?: string;
  tipoContatto?: string;
  codiceProgressivo?: string;
  generaCodiceAutomatico?: boolean;
}

/**
 * Filters for rubrica queries
 */
export interface RubricaFilters {
  nome?: string;
  codice?: string;
}

/**
 * Get all contatti from rubrica with optional filters
 */
const getContatti = async (filters?: RubricaFilters): Promise<ContattoRubrica[]> => {
  const params = new URLSearchParams();

  if (filters?.nome) {
    params.append('nome', filters.nome);
  }
  if (filters?.codice) {
    params.append('codice', filters.codice);
  }

  const queryString = params.toString();
  const url = queryString ? `/rubrica?${queryString}` : '/rubrica';

  const response = await api.get<ContattoRubrica[]>(url);
  return response.data;
};

/**
 * Get a single contatto by ID
 */
const getContattoById = async (id: number): Promise<ContattoRubrica> => {
  const response = await api.get<ContattoRubrica>(`/rubrica/${id}`);
  return response.data;
};

/**
 * Transform empty strings to null for optional fields
 * This prevents validation errors on empty optional fields
 */
const cleanContattoData = (contatto: ContattoRequest): Record<string, unknown> => {
  return {
    nome: contatto.nome,
    email: contatto.email?.trim() || null,
    telefono: contatto.telefono?.trim() || null,
    partitaIva: contatto.partitaIva?.trim() || null,
    codiceFiscale: contatto.codiceFiscale?.trim() || null,
    indirizzo: contatto.indirizzo?.trim() || null,
    citta: contatto.citta?.trim() || null,
    provincia: contatto.provincia?.trim() || null,
    cap: contatto.cap?.trim() || null,
    note: contatto.note?.trim() || null,
    tipoContatto: contatto.tipoContatto || 'PRIVATO',
    codiceProgressivo: contatto.codiceProgressivo?.trim() || null,
    generaCodiceAutomatico: contatto.generaCodiceAutomatico ?? true,
  };
};

/**
 * Create a new contatto in rubrica
 */
const createContatto = async (contatto: ContattoRequest): Promise<ContattoRubrica> => {
  const cleanedData = cleanContattoData(contatto);
  const response = await api.post<ContattoRubrica>('/rubrica', cleanedData);
  return response.data;
};

/**
 * Update an existing contatto
 */
const updateContatto = async (
  id: number,
  contatto: ContattoRequest
): Promise<ContattoRubrica> => {
  const cleanedData = cleanContattoData(contatto);
  const response = await api.put<ContattoRubrica>(`/rubrica/${id}`, cleanedData);
  return response.data;
};

/**
 * Delete a contatto from rubrica
 */
const deleteContatto = async (id: number): Promise<void> => {
  await api.delete(`/rubrica/${id}`);
};

/**
 * Get the next automatic codice progressivo
 */
const getNextCodice = async (): Promise<string> => {
  const response = await api.get<string>('/rubrica/next-codice');
  return response.data;
};

/**
 * Export Rubrica to CSV
 */
const exportRubrica = async (): Promise<void> => {
  const response = await api.get('/rubrica/export', {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const contentDisposition = response.headers['content-disposition'];
  let filename = 'rubrica.csv';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const rubricaService = {
  getContatti,
  getContattoById,
  createContatto,
  updateContatto,
  deleteContatto,
  getNextCodice,
  exportRubrica,
};

export default rubricaService;
